/**
 * Infinite Scroll Loader Component
 * Automatically loads more products when user scrolls near bottom of page
 */

(function() {
  'use strict';

  // Define the custom element
  class InfiniteScrollLoader extends HTMLElement {
    constructor() {
      super();
      this.isLoading = false;
      this.hasMorePages = true;
      this.currentPage = 1;
      this.totalPages = 1;
      this.sectionId = '';
      this.observer = null;
      this.abortController = null;
    }

    connectedCallback() {
      // Get pagination data from attributes
      this.currentPage = parseInt(this.getAttribute('current-page') || '1');
      this.totalPages = parseInt(this.getAttribute('total-pages') || '1');
      this.sectionId = this.getAttribute('section-id') || '';
      this.hasMorePages = this.currentPage < this.totalPages;

      // If no more pages, remove component
      if (!this.hasMorePages) {
        this.remove();
        return;
      }

      // Setup IntersectionObserver
      const options = {
        root: null,
        rootMargin: '200px',
        threshold: 0
      };

      this.observer = new IntersectionObserver(
        this.onSentinelVisible.bind(this),
        options
      );

      // Observe the sentinel element
      const sentinel = this.querySelector('.infinite-scroll__sentinel');
      if (sentinel) {
        console.log('Observing sentinel element');
        this.observer.observe(sentinel);
      } else {
        console.error('Sentinel element not found!');
      }
    }

    disconnectedCallback() {
      // Clean up observer
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      // Abort any pending requests
      if (this.abortController) {
        this.abortController.abort();
      }
    }

    onSentinelVisible(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoading && this.hasMorePages) {
          this.loadNextPage();
        }
      });
    }

    async loadNextPage() {
      if (this.isLoading || !this.hasMorePages) {
        return;
      }

      this.isLoading = true;
      this.showLoading();

      // Create abort controller for this request
      this.abortController = new AbortController();

      try {
        // Build URL for next page
        const nextPage = this.currentPage + 1;
        const url = new URL(window.location.href);
        url.searchParams.set('page', nextPage.toString());
        url.searchParams.set('section_id', this.sectionId);

        // Dispatch loading start event
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

        // Fetch next page
        const response = await fetch(url.toString(), {
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error('Failed to load more products');
        }

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Extract product cards from the new page
        const newSection = doc.querySelector('.shopify-section');
        const newProductList = newSection?.querySelector(`#product-list-${this.sectionId}`);

        if (newProductList) {
          const newProducts = Array.from(newProductList.querySelectorAll(':scope > product-card'));

          if (newProducts.length > 0) {
            const currentProductList = document.querySelector(`#product-list-${this.sectionId}`);
            if (currentProductList) {
              // Mark before insertion so the badge web components defer to the single-pass
              // reconciliation (jkl-plp-discounts.js) instead of self-evaluating on upgrade.
              window.JKL?.markManagedCards?.(newProducts);

              newProducts.forEach((product) => {
                currentProductList.appendChild(product);
              });

              window.JKL?.notifyRegiosLazyLoad?.(newProducts, {
                uniqueKey: this.dataset.jklRegiosUniqueKey || 'default',
                collectionId: this.dataset.jklCollectionId,
              });
            }

            // Update current page
            this.currentPage = nextPage;

            // Check if there are more pages
            this.hasMorePages = this.currentPage < this.totalPages;

            // Update attributes
            this.setAttribute('current-page', this.currentPage.toString());

            // If no more pages, remove component
            if (!this.hasMorePages) {
              if (this.observer) {
                this.observer.disconnect();
              }
              this.remove();
            }
          }
        }

        // Dispatch loading end event
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Infinite scroll error:', error);
        }
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      } finally {
        this.isLoading = false;
        this.hideLoading();
      }
    }

    showLoading() {
      const loadingIndicator = this.querySelector('.infinite-scroll__loading');
      if (loadingIndicator) {
        loadingIndicator.style.display = 'flex';
      }
    }

    hideLoading() {
      const loadingIndicator = this.querySelector('.infinite-scroll__loading');
      if (loadingIndicator) {
        loadingIndicator.style.display = 'none';
      }
    }
  }

  // Register component
  if (!window.customElements.get('infinite-scroll-loader')) {
    window.customElements.define('infinite-scroll-loader', InfiniteScrollLoader);
    console.log('Infinite scroll loader component registered');
  }
})();
